import * as XLSX from "xlsx";
import { inspectTimetableWorkbook } from "./timetableWorkbookImport";
import { supabase } from "./supabase";
import {
    ExcelImportValidationError,
    type ParsedLesson,
    type TimetableImportOptions,
} from "./timetableExcelImportValidate";

export type { ExcelImportIssue, ExcelImportIssueCode } from "./timetableExcelImportValidate";
export {
    ExcelImportValidationError,
    EXCEL_IMPORT_ISSUE_GROUPS,
    isExcelImportValidationError,
} from "./timetableExcelImportValidate";

/**
 * Django `import_timetable_excel` (views.py 2946~3193) 과 동등한 동작을
 * 클라이언트에서 xlsx 파싱 + Supabase 직접 INSERT 로 수행한다.
 *
 * 주의: 클라이언트에는 트랜잭션이 없으므로 단계별 실패 시 부분 데이터가 남을 수 있다.
 * 사용자에게 노출되는 응답 모양은 Django 응답과 동일하게 유지한다.
 */

export type ImportTimetableResult = {
    success: true;
    timetable_id: number;
    new_teachers: number;
    new_subjects: number;
    new_classrooms: number;
    schedules_added: number;
    assignments_added: number;
};

const BULK_BATCH = 200;

export async function inspectTimetableFile(file: File, sheetName?: string, options: TimetableImportOptions = {}) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    return inspectTimetableWorkbook(wb, sheetName, options);
}

export async function importTimetableFromExcel(
    schoolId: number,
    file: File,
    sheetName?: string,
    options: TimetableImportOptions = {}
): Promise<ImportTimetableResult> {
    if (!supabase) throw new Error("Supabase 클라이언트가 초기화되지 않았습니다.");

    const inspected = await inspectTimetableFile(file, sheetName, options);
    if (inspected.issues.length > 0) {
        throw new ExcelImportValidationError(inspected.issues);
    }

    const classroomNamesInFile = new Set<string>(inspected.classroomNames);

    const [
        { data: existingTeachersRaw, error: tErr },
        { data: existingSubjectsRaw, error: sErr },
        { data: existingClassroomsRaw, error: cErr },
    ] = await Promise.all([
        supabase.from("schedule_manager_teacher").select("id, name").eq("school_id", schoolId),
        supabase.from("schedule_manager_subject").select("id, name").eq("school_id", schoolId),
        supabase.from("schedule_manager_classroom").select("id, grade, class_num").eq("school_id", schoolId),
    ]);
    if (tErr || sErr || cErr) {
        console.warn("Supabase importExcel prefetch error:", tErr, sErr, cErr);
        throw new Error("기존 학교 데이터를 불러오는 중 오류가 발생했습니다.");
    }
    const existingTeachers = new Map<string, number>(
        (existingTeachersRaw || []).map((r: any) => [r.name as string, r.id as number])
    );
    const existingSubjects = new Map<string, number>(
        (existingSubjectsRaw || []).map((r: any) => [r.name as string, r.id as number])
    );
    const existingClassrooms = new Map<string, number>(
        (existingClassroomsRaw || []).map((r: any) => [`${r.grade}-${r.class_num}`, r.id as number])
    );

    const classroomGradeRe = /^(\d+)[^\d]*(\d+)$/;
    let newClassroomsCount = 0;
    const classroomsToCreate: { school_id: number; grade: number; class_num: number; key: string }[] = [];
    for (const cName of classroomNamesInFile) {
        if (existingClassrooms.has(cName)) continue;
        let grade = 1;
        let classNum = 1;
        const m = cName.match(classroomGradeRe);
        if (m) {
            grade = parseInt(m[1], 10);
            classNum = parseInt(m[2], 10);
        } else if (/^\d+$/.test(cName)) {
            classNum = parseInt(cName, 10);
        }
        const canonicalKey = `${grade}-${classNum}`;
        if (existingClassrooms.has(canonicalKey)) {
            existingClassrooms.set(cName, existingClassrooms.get(canonicalKey)!);
            continue;
        }
        classroomsToCreate.push({ school_id: schoolId, grade, class_num: classNum, key: cName });
    }
    if (classroomsToCreate.length > 0) {
        const insertRows = classroomsToCreate.map(({ key: _ignored, ...row }) => row);
        const { data: created, error: ccErr } = await supabase
            .from("schedule_manager_classroom")
            .insert(insertRows)
            .select("id, grade, class_num");
        if (ccErr || !created) {
            console.warn("Supabase importExcel classrooms insert error:", ccErr);
            throw new Error("학급 자동 생성 중 오류가 발생했습니다.");
        }
        const createdById = new Map<string, number>();
        for (const c of created as any[]) createdById.set(`${c.grade}-${c.class_num}`, c.id as number);
        for (const slot of classroomsToCreate) {
            const id = createdById.get(`${slot.grade}-${slot.class_num}`);
            if (id) {
                existingClassrooms.set(slot.key, id);
                existingClassrooms.set(`${slot.grade}-${slot.class_num}`, id);
                newClassroomsCount += 1;
            }
        }
    }

    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    const endDate = new Date(today.getTime() + 180 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const nowIso = new Date().toISOString();

    // schedule_manager_timetable 의 (school_id, name) UNIQUE 제약 회피.
    // 같은 학교에서 두 번째 업로드부터 이름이 충돌하지 않도록 (2), (3) ... 자동 suffix.
    const baseName = "[업로드] 엑셀 시간표";
    const { data: existingNames } = await supabase
        .from("schedule_manager_timetable")
        .select("name")
        .eq("school_id", schoolId)
        .like("name", `${baseName}%`);
    const used = new Set<string>(((existingNames || []) as { name: string }[]).map((r) => r.name));
    let resolvedName = baseName;
    if (used.has(resolvedName)) {
        let n = 2;
        while (used.has(`${baseName} (${n})`)) n++;
        resolvedName = `${baseName} (${n})`;
    }

    const { data: timetable, error: ttErr } = await supabase
        .from("schedule_manager_timetable")
        .insert([{
            school_id: schoolId,
            name: resolvedName,
            start_date: todayStr,
            end_date: endDate,
            is_active: false,
            created_at: nowIso,
            updated_at: nowIso,
        }])
        .select("id")
        .single();
    if (ttErr || !timetable) {
        console.warn("Supabase importExcel timetable insert error:", ttErr);
        throw new Error("시간표 행을 생성하지 못했습니다.");
    }
    const timetableId = (timetable as { id: number }).id;

    try {
        return await insertImportedSchedules({
            schoolId,
            timetableId,
            lessons: inspected.lessons,
            existingTeachers,
            existingSubjects,
            existingClassrooms,
            newClassroomsCount,
        });
    } catch (err) {
        const { error: delErr } = await supabase
            .from("schedule_manager_timetable")
            .delete()
            .eq("id", timetableId)
            .eq("school_id", schoolId);
        if (delErr) {
            console.warn("Supabase importExcel timetable rollback error:", delErr);
        }
        throw err;
    }
}

async function insertImportedSchedules(args: {
    schoolId: number;
    timetableId: number;
    lessons: ParsedLesson[];
    existingTeachers: Map<string, number>;
    existingSubjects: Map<string, number>;
    existingClassrooms: Map<string, number>;
    newClassroomsCount: number;
}): Promise<ImportTimetableResult> {
    if (!supabase) throw new Error("Supabase 클라이언트가 초기화되지 않았습니다.");
    const {
        schoolId,
        timetableId,
        lessons,
        existingTeachers,
        existingSubjects,
        existingClassrooms,
        newClassroomsCount,
    } = args;

    const teachersToCreate = new Set<string>();
    const subjectsToCreate = new Set<string>();
    type ParsedCell = {
        teacherName: string;
        subjectName: string;
        classroomKey: string;
        day: string;
        period: number;
        sharedFixed: boolean;
    };
    const parsedCells: ParsedCell[] = [];

    for (const lesson of lessons) {
        if (!existingClassrooms.has(lesson.classroom)) continue;
        if (!existingTeachers.has(lesson.teacherName)) teachersToCreate.add(lesson.teacherName);
        if (!existingSubjects.has(lesson.subjectName)) subjectsToCreate.add(lesson.subjectName);
        parsedCells.push({
            teacherName: lesson.teacherName,
            subjectName: lesson.subjectName,
            classroomKey: lesson.classroom,
            day: lesson.day,
            period: lesson.period,
            sharedFixed: !!lesson.sharedFixed,
        });
    }

    let newTeachersCount = 0;
    if (teachersToCreate.size > 0) {
        const rows = Array.from(teachersToCreate).map((name) => ({ school_id: schoolId, name }));
        const { data, error } = await supabase
            .from("schedule_manager_teacher")
            .insert(rows)
            .select("id, name");
        if (error || !data) {
            console.warn("Supabase importExcel teachers insert error:", error);
            throw new Error("교사 자동 생성 중 오류가 발생했습니다.");
        }
        for (const t of data as any[]) existingTeachers.set(t.name as string, t.id as number);
        newTeachersCount = data.length;
    }

    let newSubjectsCount = 0;
    if (subjectsToCreate.size > 0) {
        const rows = Array.from(subjectsToCreate).map((name) => ({ school_id: schoolId, name }));
        const { data, error } = await supabase
            .from("schedule_manager_subject")
            .insert(rows)
            .select("id, name");
        if (error || !data) {
            console.warn("Supabase importExcel subjects insert error:", error);
            throw new Error("과목 자동 생성 중 오류가 발생했습니다.");
        }
        for (const s of data as any[]) existingSubjects.set(s.name as string, s.id as number);
        newSubjectsCount = data.length;
    }

    type ScheduleRowJson = {
        teacher_id: number;
        subject_id: number;
        classroom_id: number;
        day: string;
        period: number;
        schedule_type: "REG" | "FIX";
        is_fixed: boolean;
    };
    const scheduleRowsJson: ScheduleRowJson[] = [];
    const assignmentsCounts = new Map<string, { teacher_id: number; subject_id: number; classroom_id: number; count: number }>();
    for (const p of parsedCells) {
        const tId = existingTeachers.get(p.teacherName);
        const sId = existingSubjects.get(p.subjectName);
        const cId = existingClassrooms.get(p.classroomKey);
        if (!tId || !sId || !cId) continue;
        scheduleRowsJson.push({
            teacher_id: tId,
            subject_id: sId,
            classroom_id: cId,
            day: p.day,
            period: p.period,
            schedule_type: p.sharedFixed ? "FIX" : "REG",
            is_fixed: p.sharedFixed,
        });
        // Fixed occupancy is already counted by the curriculum/generator. A
        // teaching assignment here would request an extra movable lesson.
        if (p.sharedFixed) continue;
        const key = `${tId}-${sId}-${cId}`;
        const cur = assignmentsCounts.get(key);
        if (cur) cur.count += 1;
        else assignmentsCounts.set(key, { teacher_id: tId, subject_id: sId, classroom_id: cId, count: 1 });
    }

    // schedule_manager_schedule 의 RLS 는 schedule_type IN ('FIX','HOM') 만 INSERT 허용.
    // REG 행은 SECURITY DEFINER RPC `bulk_import_schedules` 를 통해서만 등록 가능.
    // RPC 안에서 (1) school admin (2) timetable 소유 (3) schedule_type 화이트리스트 검증 +
    // schedule_manager_timetable_schedules M2M 연결까지 한 번에 처리한다.
    let schedulesAdded = 0;
    if (scheduleRowsJson.length > 0) {
        const RPC_BATCH = 500;
        for (let i = 0; i < scheduleRowsJson.length; i += RPC_BATCH) {
            const slice = scheduleRowsJson.slice(i, i + RPC_BATCH);
            const { data, error } = await supabase.rpc("bulk_import_schedules", {
                p_school_id: schoolId,
                p_timetable_id: timetableId,
                p_rows: slice,
            });
            if (error) {
                console.warn("Supabase rpc bulk_import_schedules error:", error);
                throw new Error("스케줄 일괄 생성 중 오류가 발생했습니다.");
            }
            const result = data as { success: boolean; count?: number; error?: string };
            if (!result?.success) {
                console.warn("Supabase rpc bulk_import_schedules failed:", result);
                throw new Error(
                    result?.error?.includes("중복")
                        ? "같은 선생님 또는 학급이 같은 시간에 두 번 들어가 있습니다. 엑셀에서 겹치는 칸을 고친 뒤 다시 올려 주세요."
                        : (result?.error || "스케줄 일괄 생성 중 오류가 발생했습니다.")
                );
            }
            schedulesAdded += result.count ?? slice.length;
        }
    }

    let assignmentsAdded = 0;
    if (assignmentsCounts.size > 0) {
        const keys = Array.from(assignmentsCounts.values());
        const teacherIds = Array.from(new Set(keys.map((k) => k.teacher_id)));
        const subjectIds = Array.from(new Set(keys.map((k) => k.subject_id)));
        const classroomIds = Array.from(new Set(keys.map((k) => k.classroom_id)));

        const { data: existingAssignments } = await supabase
            .from("schedule_manager_teachingassignment")
            .select("id, teacher_id, subject_id, classroom_id, hours_per_week")
            .eq("school_id", schoolId)
            .in("teacher_id", teacherIds)
            .in("subject_id", subjectIds)
            .in("classroom_id", classroomIds);

        const existingByKey = new Map<string, { id: number; hours_per_week: number }>();
        for (const a of (existingAssignments || []) as any[]) {
            existingByKey.set(`${a.teacher_id}-${a.subject_id}-${a.classroom_id}`, {
                id: a.id,
                hours_per_week: a.hours_per_week,
            });
        }

        const toInsert: Record<string, unknown>[] = [];
        const toUpdate: { id: number; hours_per_week: number }[] = [];
        for (const k of keys) {
            const matchKey = `${k.teacher_id}-${k.subject_id}-${k.classroom_id}`;
            const existing = existingByKey.get(matchKey);
            if (!existing) {
                toInsert.push({
                    school_id: schoolId,
                    teacher_id: k.teacher_id,
                    subject_id: k.subject_id,
                    classroom_id: k.classroom_id,
                    hours_per_week: k.count,
                    is_block: false,
                    block_size: 2,
                    single_hours: 0,
                });
            } else if (existing.hours_per_week < k.count) {
                toUpdate.push({ id: existing.id, hours_per_week: k.count });
            }
        }

        if (toInsert.length > 0) {
            for (let i = 0; i < toInsert.length; i += BULK_BATCH) {
                const slice = toInsert.slice(i, i + BULK_BATCH);
                const { error } = await supabase.from("schedule_manager_teachingassignment").insert(slice);
                if (error) {
                    console.warn("Supabase importExcel assignment insert error:", error);
                    throw new Error("수업배정 보정 중 오류가 발생했습니다.");
                }
            }
            assignmentsAdded = toInsert.length;
        }
        for (const u of toUpdate) {
            const { error } = await supabase
                .from("schedule_manager_teachingassignment")
                .update({ hours_per_week: u.hours_per_week })
                .eq("id", u.id);
            if (error) {
                console.warn("Supabase importExcel assignment update error:", error);
            }
        }
    }

    return {
        success: true,
        timetable_id: timetableId,
        new_teachers: newTeachersCount,
        new_subjects: newSubjectsCount,
        new_classrooms: newClassroomsCount,
        schedules_added: schedulesAdded,
        assignments_added: assignmentsAdded,
    };
}
